import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';

/**
 * Check if the app is running in a native environment (iOS/Android)
 */
export const isNative = Capacitor.isNativePlatform();

/**
 * Get the current platform
 */
export const getPlatform = () => Capacitor.getPlatform();

/**
 * Check if running on iOS
 */
export const isIOS = () => Capacitor.getPlatform() === 'ios';

/**
 * Check if running on Android
 */
export const isAndroid = () => Capacitor.getPlatform() === 'android';

/**
 * Trigger haptic feedback
 */
export const hapticFeedback = async (style: 'light' | 'medium' | 'heavy' = 'medium') => {
  if (!isNative) return;
  
  try {
    const impactStyle = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy
    }[style];
    
    await Haptics.impact({ style: impactStyle });
  } catch (error) {
    console.warn('Haptic feedback failed:', error);
  }
};

/**
 * Trigger notification haptic
 */
export const hapticNotification = async (type: 'success' | 'warning' | 'error' = 'success') => {
  if (!isNative) return;
  
  try {
    const notificationType = {
      success: NotificationType.Success,
      warning: NotificationType.Warning,
      error: NotificationType.Error
    }[type];
    
    await Haptics.notification({ type: notificationType });
  } catch (error) {
    console.warn('Haptic notification failed:', error);
  }
};

/**
 * Trigger selection haptic (for UI interactions)
 */
export const hapticSelection = async () => {
  if (!isNative) return;
  
  try {
    await Haptics.selectionStart();
    await Haptics.selectionEnd();
  } catch (error) {
    console.warn('Haptic selection failed:', error);
  }
};

/**
 * Set status bar style
 */
export const setStatusBarStyle = async (dark: boolean) => {
  if (!isNative) return;
  
  try {
    await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });
  } catch (error) {
    console.warn('Status bar style change failed:', error);
  }
};

/**
 * Show/hide status bar
 */
export const setStatusBarVisible = async (visible: boolean) => {
  if (!isNative) return;
  
  try {
    if (visible) {
      await StatusBar.show();
    } else {
      await StatusBar.hide();
    }
  } catch (error) {
    console.warn('Status bar visibility change failed:', error);
  }
};

/**
 * Set status bar background color (Android only)
 */
export const setStatusBarColor = async (color: string) => {
  if (!isNative || !isAndroid()) return;
  
  try {
    await StatusBar.setBackgroundColor({ color });
  } catch (error) {
    console.warn('Status bar color change failed:', error);
  }
};

/**
 * Hide keyboard
 */
export const hideKeyboard = async () => {
  if (!isNative) return;
  
  try {
    await Keyboard.hide();
  } catch (error) {
    console.warn('Keyboard hide failed:', error);
  }
};

/**
 * Show keyboard
 */
export const showKeyboard = async () => {
  if (!isNative) return;
  
  try {
    await Keyboard.show();
  } catch (error) {
    console.warn('Keyboard show failed:', error);
  }
};

/**
 * Set keyboard accessory bar visibility (iOS only)
 */
export const setAccessoryBarVisible = async (visible: boolean) => {
  if (!isNative || !isIOS()) return;
  
  try {
    await Keyboard.setAccessoryBarVisible({ isVisible: visible });
  } catch (error) {
    console.warn('Accessory bar visibility change failed:', error);
  }
};

/**
 * Add keyboard event listeners
 */
export const addKeyboardListeners = (
  onShow?: (info: { keyboardHeight: number }) => void,
  onHide?: () => void
) => {
  if (!isNative) return { remove: () => {} };
  
  const showListener = onShow 
    ? Keyboard.addListener('keyboardWillShow', onShow)
    : null;
  const hideListener = onHide
    ? Keyboard.addListener('keyboardWillHide', onHide)
    : null;
  
  return {
    remove: async () => {
      if (showListener) await (await showListener).remove();
      if (hideListener) await (await hideListener).remove();
    }
  };
};
